// RoleBasedSidebar.tsx
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useLocalization } from "../contexts/LocalizationContext";
import {
  LayoutDashboard,
  ArrowLeftRight,
  Wallet,
  FileText,
  ChevronRight,
  ChevronLeft,
  Users,
  CreditCard,
  BarChart3,
  Ticket,
  LogOut,
  BookOpen,
  FileCode,
  Receipt,
  History,
  DollarSign,
  Settings,
  UserCircle,
  ChevronDown,
  Bot,
  Banknote,
  Menu,
  KeyRound,
  Activity,
  Code2,
  BugPlay,
  Search,
  Send,
  ShieldCheck,
  History,
  Shield,
  Building,
  RefreshCw,
} from "lucide-react";
import { useWithdrawalEligibility } from "../hooks/useWithdrawalEligibility";
import { useRecipientPayoutEligibility } from "../hooks/useRecipientPayoutEligibility";
import { subscriptionService } from "../services/subscriptionService";

interface RoleBasedSidebarProps {
  activeTab: string;
  setActiveTab: (tab: string) => void;
  onNavigate?: () => void;
  logout?: () => void;
  isCollapsed?: boolean;
  onToggle?: () => void;
}

interface MenuItem {
  id: string;
  label: string;
  icon: any;
  badge?: string | null;
  hasSubmenu?: boolean;
  submenu?: SubMenuItem[];
  path?: string;
  section?: string;
}

interface SubMenuItem {
  id: string;
  label: string;
  icon: any;
  path: string;
}

const RoleBasedSidebar: React.FC<RoleBasedSidebarProps> = ({
  activeTab,
  setActiveTab,
  onNavigate,
  logout,
  isCollapsed = false,
  onToggle,
}) => {
  const { user, merchantProfile, loading } = useAuth();
  const { t } = useLocalization();
  const navigate = useNavigate();
  const location = useLocation();
  const [expandedMenus, setExpandedMenus] = useState<Record<string, boolean>>({});

  const { data: eligibility, isLoading: isLoadingEligibility } = useWithdrawalEligibility();
  const hasWithdrawalAccess = eligibility?.has_withdrawal_access || false;

  const { data: recipientPayoutElig } = useRecipientPayoutEligibility();
  const hasRecipientPayoutAccess = recipientPayoutElig?.has_recipient_payout_access || false;

  const [hasCardAccess, setHasCardAccess] = useState(false);
  const [hasMobileMoneyAccess, setHasMobileMoneyAccess] = useState(true); // default true; refined async
  const [isCheckingAccess, setIsCheckingAccess] = useState(false);

  // Betting / gambling sectors are excluded from cashout for regulatory and
  // operational reasons (their outflows follow a customer-payout logic, not a
  // merchant cashout). Defensive UI check on top of backend eligibility.
  const isBettingMerchant = (() => {
    // Explicit allowlist of institution codes flagged as betting/gaming,
    // used when the `sector` column is NULL or mistagged (e.g. Pickslam-Digitain).
    const BETTING_INSTITUTION_CODES = ["isSCpic00R4lWLOpd", "iso3DGUbRLcccOypd"];
    const institutionCode = merchantProfile?.merchant?.institution_code || "";
    if (institutionCode && BETTING_INSTITUTION_CODES.includes(institutionCode)) {
      return true;
    }
    const sector = merchantProfile?.institution?.sector?.toLowerCase() || "";
    if (!sector) return false;
    return ["bet", "gam", "paris", "jeu", "casino", "lott", "wager"].some(kw =>
      sector.includes(kw)
    );
  })();

  useEffect(() => {
    const currentPath = location.pathname;
    const menuItems = getMenuItems();

    for (const item of menuItems) {
      if (item.path === currentPath) {
        setActiveTab(item.id);
        break;
      }

      if (item.submenu) {
        for (const subItem of item.submenu) {
          if (subItem.path === currentPath || currentPath.startsWith(subItem.path)) {
            setActiveTab(item.id);
            if (!isCollapsed) {
              setExpandedMenus(prev => ({
                ...prev,
                [item.id]: true
              }));
            }
            break;
          }
        }
      }
    }
  }, [location.pathname, setActiveTab, isCollapsed]);

  useEffect(() => {
    const checkSubscriptions = async () => {
      if (!user?.merchant_code) {
        setHasCardAccess(false);
        setHasMobileMoneyAccess(false);
        return;
      }

      setIsCheckingAccess(true);
      try {
        const methods = await subscriptionService.getActivePaymentMethods();
        setHasCardAccess(methods.has_card);
        setHasMobileMoneyAccess(methods.has_mobile_money);
      } catch (error) {
        console.error("Error checking subscriptions:", error);
        setHasCardAccess(false);
        // Keep mobile money optimistically true if the check fails: most
        // merchants are mobile money first, hiding everything on a network
        // hiccup would be worse than showing menus that 404 on click.
        setHasMobileMoneyAccess(true);
      } finally {
        setIsCheckingAccess(false);
      }
    };

    if (user?.merchant_code) {
      checkSubscriptions();
    }
  }, [user?.merchant_code]);

  const handleNavigation = (path: string, tabId?: string, isSubmenu: boolean = false) => {
    if (tabId) {
      setActiveTab(tabId);
    }
    navigate(path);
    if (onNavigate) {
      onNavigate();
    }
    if (isSubmenu && onNavigate) {
      setTimeout(() => {
        onNavigate();
      }, 100);
    }
  };

  const toggleSubmenu = (menuId: string, e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }

    if (isCollapsed) {
      const menuItem = getMenuItems().find(item => item.id === menuId);
      if (menuItem?.submenu && menuItem.submenu.length > 0) {
        handleNavigation(menuItem.submenu[0].path, menuId, true);
      }
      return;
    }

    setExpandedMenus(prev => ({
      ...prev,
      [menuId]: !prev[menuId]
    }));
  };

  const determineRole = (): string => {
    if (!user) return "user";

    const validRoles = ["merchant_admin", "merchant_finance", "merchant_support", "merchant_integration"];
    if (user.role && validRoles.includes(user.role)) {
      return user.role;
    }

    let niveauNum: number;
    if (typeof user.niveau === 'number') {
      niveauNum = user.niveau;
    } else if (typeof user.niveau === 'string') {
      niveauNum = parseInt(user.niveau, 10);
      if (isNaN(niveauNum)) {
        niveauNum = 0;
      }
    } else {
      niveauNum = 0;
    }

    if (niveauNum === 0) return "merchant_admin";
    if (niveauNum === 1) return "merchant_finance";
    if (niveauNum === 2) return "merchant_support";
    if (niveauNum === 3) return "merchant_integration";
    return "user";
  };

  const getMenuItems = (): MenuItem[] => {
    const baseItems: MenuItem[] = [
      {
        id: "dashboard",
        label: t("nav.dashboard"),
        icon: LayoutDashboard,
        path: "/dashboard",
        section: "main"
      },
    ];

    if (!user) {
      return baseItems;
    }

    const roleKey = determineRole();

    // ─── Cards product section (subscription-gated) ───────────────────
    // Virtual card balances live under /balances (Cards tab).
    // Card transactions live under /reports/transactions (Cards tab).
    // Card cashout lives under /withdrawal (Cards tab).
    const cardItems: MenuItem[] = hasCardAccess ? [
      ...(roleKey === "merchant_finance" ? [{
        id: "card-history",
        label: t("nav.walletHistory"),
        icon: History,
        path: "/cards/history",
        section: "cards",
      }] : []),
    ] : [];

    // ─── Wallets section ──────────────────────────────────────────────
    // Now that Balances and Cashout are channel-agnostic pages (internal
    // Mobile Money / Cards tabs), entries are shown whenever the merchant
    // has access to AT LEAST ONE channel. Smart path defaults to the
    // available tab so a cards-only merchant lands directly on Cards.
    const hasAnyWalletAccess = hasMobileMoneyAccess || hasCardAccess;
    const canCashout = (hasWithdrawalAccess || hasCardAccess) && !isBettingMerchant;
    const balancesPath  = hasMobileMoneyAccess ? "/balances"  : "/balances?tab=cards";
    const cashoutPath   = hasWithdrawalAccess  ? "/withdrawal" : "/withdrawal?tab=cards";

    const mobileMoneyAdminItems: MenuItem[] = hasAnyWalletAccess ? [
      {
        id: "balances",
        label: t("nav.balances"),
        icon: Wallet,
        path: balancesPath,
        section: "mobile_money",
      },
      // Transfers are mobile-money-only operations
      // (intra/inter operator, wallet↔bank — no card equivalent).
      // Expanded as a submenu so each type (intra, inter, card→mobile,
      // wallet→bank, history) gets its own entry. The /transfers page
      // reads ?tab= to render the matching panel — same page under the
      // hood, so existing deep links to /transfers keep working.
      ...(hasMobileMoneyAccess ? [{
        id: "transfers",
        label: t("nav.transfers"),
        icon: ArrowLeftRight,
        // Keep the base path so the parent entry stays visually highlighted
        // when the user lands on /transfers with no tab (default: intra),
        // and when any pre-submenu bookmark hits the page directly.
        path: "/transfers",
        section: "mobile_money",
        hasSubmenu: true,
        submenu: [
          { id: "transfers-intra",   label: t("transfers.intraOperator"),          icon: ArrowLeftRight, path: "/transfers?tab=wallet-to-wallet" },
          { id: "transfers-inter",   label: t("transfers.interOperator"),          icon: RefreshCw,      path: "/transfers?tab=inter-operator" },
          { id: "transfers-card",    label: t("transfers.cardToMobile.tab") || "Card → Mobile Money", icon: CreditCard, path: "/transfers?tab=card-to-mobile" },
          { id: "transfers-w2b",     label: t("transfers.walletToBank"),           icon: Building,       path: "/transfers?tab=wallet-to-bank" },
          { id: "transfers-history", label: t("transfers.history"),                icon: History,        path: "/transfers?tab=history" },
        ],
      }] : []),
      // Cashout is admin-only and excluded for betting merchants.
      // Visible as soon as cashout is possible on either channel.
      ...(canCashout ? [{
        id: "withdrawal",
        label: t("nav.withdrawal"),
        icon: Banknote,
        path: cashoutPath,
        section: "mobile_money",
      }] : []),
      // Reversements (RECIPIENT_PAYOUT_API) — gated by drc_merchant_services
      // toggled per merchant in backoffice merchant-control.
      ...(hasRecipientPayoutAccess ? [{
        id: "recipient-payouts",
        label: "Reversements",
        icon: Send,
        path: "/recipient-payouts",
        section: "mobile_money",
      }] : []),
    ] : [];

    const roleSpecificItems: Record<string, MenuItem[]> = {
      merchant_admin: [
        ...mobileMoneyAdminItems,
        ...cardItems,
        {
          id: "reports",
          label: t("nav.reports"),
          icon: FileText,
          path: "/reports/transactions",
          section: "analytics",
        },
        {
          id: "failures",
          label: t("nav.failureAnalysis") || "Analyse des echecs",
          icon: BugPlay,
          path: "/failures",
          section: "analytics",
        },
        {
          id: "lookup",
          label: t("nav.lookup") || "Verifier une transaction",
          icon: Search,
          path: "/lookup",
          section: "analytics",
        },
        ...(!isBettingMerchant ? [{
          id: "cashout-settings",
          label: t("nav.cashoutSettings") || "Paramètres de décaissement",
          icon: Settings,
          path: "/cashout-settings",
          section: "management",
        }] : []),
        {
          id: "tickets",
          label: t("nav.tickets"),
          icon: Ticket,
          path: "/tickets",
          section: "management"
        },
        {
          id: "account",
          label: t("nav.account"),
          icon: CreditCard,
          path: "/account",
          section: "management"
        },
        // ---- Access & Security section (grouped together for visual grouping) ----
        {
          id: "team",
          label: t("nav.team") || "Team members",
          icon: Users,
          path: "/team",
          section: "access",
        },
        {
          id: "custom-roles",
          label: t("nav.customRoles") || "Custom roles",
          icon: Shield,
          path: "/settings/roles",
          section: "access",
        },
        {
          id: "audit-log",
          label: t("nav.auditLog") || "Audit log",
          icon: History,
          path: "/settings/audit-log",
          section: "access",
        },
        {
          id: "twofa",
          label: t("nav.twofa") || "Two-factor",
          icon: ShieldCheck,
          path: "/settings/2fa",
          section: "access",
        },
      ],
      merchant_finance: [
        ...(hasAnyWalletAccess ? [
          {
            id: "balances",
            label: t("nav.balances"),
            icon: Wallet,
            path: balancesPath,
            section: "mobile_money",
          },
        ] : []),
        ...(hasMobileMoneyAccess ? [
          {
            id: "transfers",
            label: t("nav.transfers"),
            icon: ArrowLeftRight,
            path: "/transfers",
            section: "mobile_money",
          },
        ] : []),
        ...cardItems,
        {
          id: "reports",
          label: t("nav.reports"),
          icon: FileText,
          path: "/reports/transactions",
          section: "analytics",
        },
        {
          id: "analytics",
          label: t("nav.analytics"),
          icon: BarChart3,
          path: "/analytics",
          section: "analytics"
        },
        {
          id: "failures",
          label: t("nav.failureAnalysis") || "Analyse des echecs",
          icon: BugPlay,
          path: "/failures",
          section: "analytics",
        },
        {
          id: "lookup",
          label: t("nav.lookup") || "Verifier une transaction",
          icon: Search,
          path: "/lookup",
          section: "analytics",
        },
        {
          id: "tickets",
          label: t("nav.tickets"),
          icon: Ticket,
          path: "/tickets",
          section: "management"
        },
        {
          id: "twofa",
          label: t("nav.twofa") || "Two-factor",
          icon: ShieldCheck,
          path: "/settings/2fa",
          section: "access",
        },
      ],
      merchant_support: [
        {
          id: "reports",
          label: t("nav.reports"),
          icon: FileText,
          path: "/reports/transactions",
          section: "analytics"
        },
        {
          id: "lookup",
          label: t("nav.lookup") || "Verifier une transaction",
          icon: Search,
          path: "/lookup",
          section: "analytics",
        },
        {
          id: "tickets",
          label: t("nav.tickets"),
          icon: Ticket,
          path: "/tickets",
          section: "management"
        },
        {
          id: "twofa",
          label: t("nav.twofa") || "Two-factor",
          icon: ShieldCheck,
          path: "/settings/2fa",
          section: "access",
        },
      ],
      merchant_integration: [
        {
          id: "api-keys",
          label: t("nav.apiKeys") || "API Keys",
          icon: KeyRound,
          path: "/developers/api-keys",
          section: "developers",
        },
        {
          id: "ip-whitelist",
          label: t("nav.ipWhitelist") || "IP Whitelist",
          icon: ShieldCheck,
          path: "/developers/ip-whitelist",
          section: "developers",
        },
        {
          id: "audit-log",
          label: t("nav.auditLog") || "Audit log",
          icon: History,
          path: "/settings/audit-log",
          section: "access",
        },
        {
          id: "twofa",
          label: t("nav.twofa") || "Two-factor",
          icon: ShieldCheck,
          path: "/settings/2fa",
          section: "access",
        },
        {
          id: "events",
          label: t("nav.events") || "Events",
          icon: Activity,
          path: "/reports/transactions",
          section: "developers",
        },
        {
          id: "failures",
          label: t("nav.failureAnalysis") || "Failure analysis",
          icon: BugPlay,
          path: "/failures",
          section: "developers",
        },
        {
          id: "lookup",
          label: t("nav.lookup") || "Transaction lookup",
          icon: Search,
          path: "/lookup",
          section: "developers",
        },
        {
          id: "documentation",
          label: t("nav.documentation") || "Documentation",
          icon: BookOpen,
          path: "/documentation",
          section: "resources",
        },
        {
          id: "tickets",
          label: t("nav.support") || "Support",
          icon: Ticket,
          path: "/tickets",
          section: "resources",
        },
      ],
    };

    const specificItems = roleSpecificItems[roleKey] || [];
    return [...baseItems, ...specificItems];
  };

  const menuItems = getMenuItems();

  // Group items by section for visual separation
  const groupedItems = () => {
    const groups: { section: string; items: MenuItem[] }[] = [];
    let currentSection = "";

    menuItems.forEach(item => {
      const section = item.section || "main";
      if (section !== currentSection) {
        currentSection = section;
        groups.push({ section, items: [item] });
      } else {
        groups[groups.length - 1].items.push(item);
      }
    });

    return groups;
  };

  const sectionLabels: Record<string, string> = {
    main: t("sidebar.section.overview") || "OVERVIEW",
    mobile_money: t("sidebar.section.wallets") || "WALLETS",
    cards: t("sidebar.section.cards") || "CARDS",
    finance: t("sidebar.section.finance") || "FINANCE",
    analytics: t("sidebar.section.analytics") || "ANALYTICS",
    management: t("sidebar.section.management") || "MANAGEMENT",
    access: t("sidebar.section.access") || "ACCESS & SECURITY",
    developers: t("sidebar.section.developers") || "DEVELOPERS",
    resources: t("sidebar.section.resources") || "RESOURCES",
  };

  if (loading) {
    return (
      <div className={`
        relative h-screen flex flex-col items-center justify-center transition-all duration-500
        ${isCollapsed ? 'w-20' : 'w-64'}
        bg-[#0C111D]
        border-r border-white/[0.06]
        overflow-hidden
      `}>
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
          <p className="text-xs text-slate-500">{t("common.loading")}</p>
        </div>
      </div>
    );
  }

  if (!user) return null;

  const displayedRole = determineRole();
  const roleDisplayText = displayedRole === "merchant_admin" ? t("role.admin") :
                         displayedRole === "merchant_finance" ? t("role.finance") :
                         displayedRole === "merchant_support" ? t("role.support") :
                         displayedRole === "merchant_integration" ? (t("role.integration") || "Integration") : t("role.user");

  const roleColor = displayedRole === "merchant_admin" ? "text-indigo-400 bg-indigo-50 border-indigo-200" :
                    displayedRole === "merchant_finance" ? "text-teal-600 bg-teal-50 border-teal-200" :
                    displayedRole === "merchant_support" ? "text-amber-600 bg-amber-50 border-amber-200" :
                    displayedRole === "merchant_integration" ? "text-purple-600 bg-purple-50 border-purple-200" :
                    "text-slate-500 bg-slate-50 border-slate-200";

  return (
    <div className={`
      relative h-screen flex flex-col transition-all duration-300 ease-in-out
      ${isCollapsed ? 'w-20' : 'w-64'}
      bg-[#0C111D]
      border-r border-white/[0.06]
      overflow-hidden
    `}>
      {/* Logo Section */}
      <div className="px-4 pt-4 pb-3 relative">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <img
              src="/LOGO_MOKO_fd_vert.png"
              alt="MOKO Afrika"
              className="h-9 w-9 rounded-lg flex-shrink-0 shadow-md object-cover"
            />

            {!isCollapsed && (
              <div className="flex flex-col leading-tight">
                <span className="text-[13px] font-bold text-white tracking-tight">
                  MOKO AFRIKA
                </span>
                <span className="text-[9px] text-indigo-400 font-semibold tracking-[0.15em] uppercase">
                  Merchant Suite
                </span>
              </div>
            )}
          </div>

          {onToggle && (
            <button
              onClick={onToggle}
              className="p-1.5 rounded-md text-slate-500 hover:text-white hover:bg-white/5 transition-colors"
              title={isCollapsed ? t("sidebar.expand") : t("sidebar.collapse")}
            >
              <Menu className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>


      {/* Navigation — Paystack-style flat list with subtle separators between groups */}
      <nav className="flex-1 overflow-y-auto scrollbar-thin px-3 pt-4 pb-2 relative">
        {groupedItems().map((group, groupIndex) => {
          return (
          <div key={groupIndex}>
            {/* Group separator — replaces UPPERCASE section labels */}
            {groupIndex > 0 && (
              <div className={isCollapsed ? "py-2 px-2" : "py-2 px-3"}>
                <div className="h-px bg-white/[0.06]" />
              </div>
            )}

            <div className="space-y-0.5">
              {group.items.map((item) => {
                const Icon = item.icon;
                const isActive = activeTab === item.id || location.pathname === item.path;
                const isExpanded = expandedMenus[item.id];
                const currentPath = location.pathname;

                let isAnySubActive = false;
                if (item.submenu) {
                  isAnySubActive = item.submenu.some(subItem => {
                    // Sub-items that use a query string (e.g. "/transfers?tab=wallet-to-bank")
                    // need both the pathname AND the query to match. Items without a query
                    // keep the original path-based check for backward compat.
                    const [subPath, subQuery] = subItem.path.split("?");
                    if (currentPath !== subPath && !currentPath.startsWith(subPath)) return false;
                    if (!subQuery) return true;
                    const current = new URLSearchParams(location.search);
                    const expected = new URLSearchParams(subQuery);
                    for (const [k, v] of expected.entries()) {
                      if (current.get(k) !== v) return false;
                    }
                    return true;
                  });
                }

                const isHighlighted = isActive || isAnySubActive;

                return (
                  <div key={item.id}>
                    {item.hasSubmenu ? (
                      <>
                        <button
                          onClick={(e) => {
                            if (isCollapsed) {
                              if (item.submenu && item.submenu.length > 0) {
                                handleNavigation(item.submenu[0].path, item.id, true);
                              }
                            } else {
                              toggleSubmenu(item.id, e);
                            }
                          }}
                          className={`
                            w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-md
                            transition-all duration-200
                            ${isCollapsed ? 'justify-center' : ''}
                            ${isHighlighted
                              ? 'bg-indigo-500/10 text-indigo-200 border border-indigo-400/30'
                              : 'text-slate-400 hover:text-white hover:bg-white/5 border border-transparent'
                            }
                          `}
                          title={isCollapsed ? item.label : ''}
                        >
                          <Icon className={`h-[16px] w-[16px] flex-shrink-0 ${isHighlighted ? 'text-indigo-400' : ''}`} />

                          {!isCollapsed && (
                            <>
                              <span className="flex-1 text-[13px] font-medium text-left">{item.label}</span>
                              <ChevronDown className={`
                                h-3.5 w-3.5 transition-transform duration-200
                                ${isExpanded ? 'rotate-180 text-slate-400' : 'text-slate-500'}
                              `} />
                            </>
                          )}
                        </button>

                        {/* Submenu */}
                        {!isCollapsed && isExpanded && item.submenu && (
                          <div className="mt-0.5 ml-4 pl-3 border-l border-white/10 space-y-0.5">
                            {item.submenu.map((subItem) => {
                              const SubIcon = subItem.icon;
                              const isSubActive = (() => {
                                const [subPath, subQuery] = subItem.path.split("?");
                                if (currentPath !== subPath && !currentPath.startsWith(subPath)) return false;
                                if (!subQuery) return true;
                                const current = new URLSearchParams(location.search);
                                const expected = new URLSearchParams(subQuery);
                                for (const [k, v] of expected.entries()) {
                                  if (current.get(k) !== v) return false;
                                }
                                return true;
                              })();

                              return (
                                <button
                                  key={subItem.id}
                                  onClick={() => handleNavigation(subItem.path, item.id, true)}
                                  className={`
                                    w-full flex items-center gap-3 px-3 py-2 rounded-lg
                                    transition-all duration-200 text-sm
                                    ${isSubActive
                                      ? 'text-indigo-700 bg-indigo-50'
                                      : 'text-slate-400 hover:text-white hover:bg-white/5'
                                    }
                                  `}
                                >
                                  <SubIcon className={`h-4 w-4 flex-shrink-0 ${isSubActive ? 'text-indigo-400' : ''}`} />
                                  <span className="font-medium">{subItem.label}</span>
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </>
                    ) : (
                      <button
                        onClick={() => {
                          if (item.path) {
                            handleNavigation(item.path, item.id, false);
                          }
                        }}
                        className={`
                          w-full flex items-center gap-3 px-3 py-2 rounded-lg
                          transition-all duration-200
                          ${isCollapsed ? 'justify-center' : ''}
                          ${isHighlighted
                            ? 'bg-indigo-500/10 text-indigo-200'
                            : 'text-slate-400 hover:text-white hover:bg-white/5'
                          }
                        `}
                        title={isCollapsed ? item.label : ''}
                      >
                        <div className="relative flex items-center justify-center">
                          {isHighlighted && (
                            <div className="absolute -left-[21px] w-[3px] h-5 bg-indigo-500 rounded-r-full shadow-[0_0_8px_rgba(99,102,241,0.4)]"></div>
                          )}
                          <Icon className={`h-[16px] w-[16px] flex-shrink-0 ${isHighlighted ? 'text-indigo-400' : ''}`} />
                        </div>

                        {!isCollapsed && (
                          <span className="text-[13px] font-medium">{item.label}</span>
                        )}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          );
        })}
      </nav>

      {/* Bottom Section */}
      <div className="border-t border-white/[0.06] p-3 space-y-0.5">
        {/* Settings */}
        <button
          onClick={() => handleNavigation("/settings", "settings")}
          className={`
            w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-md
            transition-all duration-200
            ${isCollapsed ? 'justify-center' : ''}
            ${location.pathname === '/settings'
              ? 'bg-indigo-500/10 text-indigo-200 border border-indigo-400/30'
              : 'text-slate-400 hover:text-white hover:bg-white/5 border border-transparent'
            }
          `}
          title={isCollapsed ? t("nav.settings") || "Settings" : ''}
        >
          <Settings className={`h-[16px] w-[16px] flex-shrink-0 ${location.pathname === '/settings' ? 'text-indigo-400' : ''}`} />
          {!isCollapsed && (
            <span className="text-[13px] font-medium">{t("nav.settings") || "Settings"}</span>
          )}
        </button>

        {/* Docs */}
        <button
          onClick={() => handleNavigation("/docs", "docs")}
          className={`
            w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-md
            transition-all duration-200
            ${isCollapsed ? 'justify-center' : ''}
            ${location.pathname === '/docs'
              ? 'bg-indigo-500/10 text-indigo-200 border border-indigo-400/30'
              : 'text-slate-400 hover:text-white hover:bg-white/5 border border-transparent'
            }
          `}
          title={isCollapsed ? t("nav.docs") : ''}
        >
          <BookOpen className={`h-[16px] w-[16px] flex-shrink-0 ${location.pathname === '/docs' ? 'text-indigo-400' : ''}`} />
          {!isCollapsed && (
            <span className="text-[13px] font-medium">{t("nav.docs")}</span>
          )}
        </button>

        {/* Separator */}
        <div className="pt-1">
          <div className="h-px bg-white/[0.06] mb-2"></div>
        </div>

        {/* Logout */}
        <button
          onClick={() => {
            logout?.();
            navigate("/login");
          }}
          className={`
            w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-md
            transition-all duration-200
            ${isCollapsed ? 'justify-center' : ''}
            text-slate-400 hover:text-red-400 hover:bg-red-500/10
          `}
          title={isCollapsed ? t("common.logout") : ""}
        >
          <LogOut className="h-[16px] w-[16px] flex-shrink-0" />
          {!isCollapsed && (
            <span className="text-[13px] font-medium">{t("common.logout")}</span>
          )}
        </button>
      </div>
    </div>
  );
};

export default RoleBasedSidebar;
